import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaSearch, FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';

function ProductList() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(100);

    // For modal state
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [productId, setProductId] = useState(null);
    const [, setProductName] = useState('');
    const [productImage, setProductImage] = useState(null);
    const [categoryId, setCategoryId] = useState('');
    const [imagePreview, setImagePreview] = useState(null);

    const fetchProducts = async () => {
        const token = localStorage.getItem('authToken');
        if (token) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/getproduct`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch products');
                }

                const result = await response.json();
                setProducts(result.products || []);
            } catch (error) {
                console.error('Error fetching products:', error);
            }
        }
    };

    const fetchCategories = async () => {
        const token = localStorage.getItem('authToken');
        if (token) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/getcategories`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch categories');
                }

                const result = await response.json();
                setCategories(result.data || []);
            } catch (error) {
                console.error('Error fetching categories:', error);
            }
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const filteredProducts = products.filter((product) =>
        product && Object.values(product).some(value =>
            String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
    );    

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Handle product form submission (Add/Edit)
    const handleAddOrEditProduct = async () => {
        const token = localStorage.getItem('authToken');
        const formData = new FormData();
        formData.append('category_id', categoryId);
        formData.append('product_image', productImage);

        try {
            const method = isEditing ? 'PUT' : 'POST';
            const url = isEditing
                ? `${process.env.REACT_APP_API_URL}/updateproducts/${productId}`
                : `${process.env.REACT_APP_API_URL}/addproduct`;

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(isEditing ? 'Failed to update product' : 'Failed to add product');
            }

            const result = await response.json();
            if (isEditing) {
                setProducts(products.map((prod) => (prod.product_id === productId ? result.data : prod))); // Update edited product
            } else {
                setProducts([...products, result.data]); // Add new product
            }
            fetchProducts();
            setShowModal(false);
            resetForm();
        } catch (error) {
            console.error('Error adding/updating product:', error);
        }
    };

    // Handle the Edit button click
    const handleEditClick = (product) => {
        setProductId(product.product_id);
        setCategoryId(product.category_id);
        setProductImage(null);
        setImagePreview(`${product.product_image}`);
        setIsEditing(true);
        setShowModal(true);
    };

    // Handle product deletion
    const handleDeleteClick = (productId) => {
        setProductId(productId);
        setShowDeleteModal(true);
    };

    const handleDeleteProduct = async () => {
        const token = localStorage.getItem('authToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/deleteproducts/${productId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to delete product');
            }
            fetchProducts();
            setProducts(products.filter((product) => product.product_id !== productId));
            setShowDeleteModal(false);
        } catch (error) {
            console.error('Error deleting product:', error);
        }
    };

    // Handle image change (for preview)
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setProductImage(file);
            setImagePreview(URL.createObjectURL(file)); // Create preview URL for the image
        }
    };

    // Reset form fields
    const resetForm = () => {
        setProductName('');
        setProductImage(null);
        setCategoryId('');
        setImagePreview(null);
        setProductId(null);
        setIsEditing(false);
    };

    // Handle modal close (reset form)
    const handleModalClose = () => {
        resetForm();
        setShowModal(false);
    };

    return (
        <div className="container" style={{ marginTop: '80px' }}>
            <h2 className="text-primary">Product List</h2>

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="input-group me-3" style={{ maxWidth: '300px' }}>
                    <span className="input-group-text bg-primary text-white">
                        <FaSearch />
                    </span>
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search products..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="btn btn-success" onClick={() => setShowModal(true)}>
                    <FaPlus /> Add Product
                </button>
            </div>

            {/* Table View */}
            <table className="table table-bordered table-striped">
                <thead className="table-primary">
                    <tr>
                        <th>ID</th>
                        <th>Category</th>
                        <th>Image</th>
                        <th>Created At</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {currentProducts.map((product) => (
                        <tr key={product.product_id}>
                            <td>{product.product_id}</td>
                            <td>{product.category}</td>
                            <td>
                                <img
                                    src={`${product.product_image}`}
                                    alt="alt"
                                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                                />
                            </td>
                            <td>{new Date(product.product_createdAt).toLocaleDateString()}</td>
                            <td>
                                <button className="btn btn-sm btn-outline-success me-2" onClick={() => handleEditClick(product)}>
                                    <FaEdit /> Edit
                                </button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(product.product_id)}>
                                    <FaTrash /> Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            <nav>
                <ul className="pagination justify-content-center">
                    {[...Array(totalPages)].map((_, index) => (
                        <li key={index} className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => handlePageChange(index + 1)}>
                                {index + 1}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            {/* Modal for Adding/Editing Product */}
            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? 'Edit Product' : 'Add Product'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>

                        <Form.Group className="mb-3">
                            <Form.Label>Category</Form.Label>
                            <Form.Select
                                value={categoryId}
                                onChange={(e) => setCategoryId(e.target.value)}
                            >
                                <option value="">Select Category</option>
                                {categories.map((category) => (
                                    <option key={category.id} value={category.id}>
                                        {category.category}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Product Image</Form.Label>
                            <Form.Control
                                type="file"
                                onChange={handleImageChange}
                                accept="image/*"
                            />
                            {imagePreview && <img src={imagePreview} alt="preview" style={{ width: '100px', height: '100px' }} />}
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleModalClose}>Close</Button>
                    <Button variant="primary" onClick={handleAddOrEditProduct}>{isEditing ? 'Update' : 'Add'}</Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete this product?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDeleteProduct}>Delete</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ProductList;
